import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, X, ArrowLeft, ChevronDown, Menu, MapPin, Settings } from 'lucide-react';
import { PatientCard } from './PatientCard';
import { PatientDetail } from './PatientDetail';
import { FluencyAIBar } from './FluencyAIBar';
import { SettingsModal } from './SettingsModal';
import { SupportButton } from './SupportButton';
import { useAuth } from './AuthProvider';
import { fetchWithNoCache } from './fetchUtils';
import fluencyLogo from '../../src/assets/prefaceWHITE.png';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { motion, AnimatePresence } from 'framer-motion';

// API functions
const fetchPatients = async (location) => {
  const response = await fetchWithNoCache(`/api/patients?location=${location}`, {
    credentials: 'include'
  });
  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('unauthorized');
    }
    throw new Error('Failed to fetch patients');
  }
  return response.json();
};

const fetchUserSettings = async () => {
  const response = await fetchWithNoCache('/api/user/settings', {
    credentials: 'include'
  });
  
  if (!response.ok) {
    if (response.status === 401) throw new Error('unauthorized');
    throw new Error('Failed to fetch user settings');
  }
  
  return response.json();
};

const saveUserSettings = async (settings) => {
  const response = await fetch('/api/user/settings', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(settings)
  });
  
  if (!response.ok) throw new Error('Failed to save user settings');
  return response.json();
};

const createPatient = async (location) => {
  const now = new Date().toISOString();
  const response = await fetch('/api/patients', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      first_name: 'First',
      last_name: 'Last',
      date_of_birth: '01/01/2025',
      patient_id: Math.random().toString(36).substr(2, 9),
      created_at: now,
      displayTime: now,  // Add this critical field for sorting
      sentToEHR: false,  // Explicitly state this for filtering
      isScheduled: false, // Include this for completeness
      location: location
    })
  });

  if (!response.ok) throw new Error('Failed to create patient');
  return response.json();
};

const createSoapNote = async (patientId) => {
  // Get the patient to check location
  const response = await fetch(`/api/patients/${patientId}`, {
    credentials: 'include'
  });
  
  if (!response.ok) throw new Error('Failed to get patient');
  const patient = await response.json();
  
  // Determine data structure based on location
  let soapData;
  
  if (patient.location === 'siliconbeach') {
    // SOAP format for Silicon Beach
    soapData = {
      subjective: [''],
      objective: [''],
      assessment: [''],
      plan: [''],
      physical_exam: [''],
      suggested_tests: [''],
      icd_codes: [''],
      after_visit_summary: [''],
      format: 'soap'
    };
  } else {
    // Standard format for Evergreen and other locations
    soapData = {
      hpi: [''],
      ros: [''],
      assessment_plan: [''],
      physical_exam: [''],
      suggested_tests: [''],
      icd_codes: [''],
      after_visit_summary: [''],
      format: 'standard'
    };
  }
  
  // Create the SOAP note with the appropriate format
  const createResponse = await fetch(`/api/patients/${patientId}/soap`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(soapData)
  });

  if (!createResponse.ok) throw new Error('Failed to create SOAP note');
  return createResponse.json();
};

const LocationSelector = ({ currentLocation, availableLocations, onLocationChange }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  
  // Function to format location names for display
  const formatLocationName = (location) => {
    if (!location) return '';
    
    // Map of location IDs to their display names
    const locationDisplayNames = {
      'evergreen': 'Evergreen',
      'siliconbeach': 'Silicon Beach',
      'weho': 'WeHo',
      'brentwood': 'Brentwood',
      'skindrmbw': 'Skin Dermatology Brentwood',
      'skindrmweho': 'Skin Dermatology WeHo',
      'dtla': 'DTLA',
      'oceanpark': 'Ocean Park',
      'confdemo': 'SERUCA',
      'cedarhills': 'Cedar Hills',
      'hillsboro': 'Hillsboro',
      'hallandale': 'Hallandale',
      'speer': 'Speer',
      'wylie': 'Wylie',
      'iec': 'IEC',
      'lincoln': 'Lincoln',
      'wicker': 'Wicker',
      'logan': 'Logan',
      '2418': '2418',
      'diversey': 'Diversey',
      'brockton': 'Brockton',
      'sonoma': 'Sonoma',
      'napa': 'Napa',
      'benicia': 'Benicia',
      'petaluma': 'Petaluma',
      'miracletouch': 'Miracle Touch',
      'modernmdfulton': 'Fulton',
      'modernmdflatbush': 'Flatbush',
      'modernmdfreshpond': 'Fresh Pond',
      'modernmdgraham': 'Graham',
      'modernmdjh': 'Jackson Heights',
      'modernmdjamaica': 'Jamaica',
      'modernmdknickerbrocker': 'Knickerbocker',
      'modernmdlefferts': 'Lefferts',
      'modernmdlibertyave': 'Liberty Ave',
      'modernmdnorthutica': 'North Utica',
      'modernmdsunsetpark': 'Sunset Park',
      'modernmdutica': 'Utica',
      //'pulsemd': 'Pulse MD',
      'wappingers': 'Wappingers',
      'mahopac': 'Mahopac',
      'mohegan_lake': 'Mohegan Lake',
      'poughkeepsie': 'Poughkeepsie',
      'hudson_heritage': 'Hudson Heritage',
      'briarcliff': 'Briarcliff',
      'valvista': 'Val Vista',
      'queencreek': 'Queen Creek',
      'higley': 'Higley',
      'gilbert': 'Gilbert',
      'bensalem': 'Bensalem',
      'blue_bell': 'Blue Bell',
      'brookhaven': 'Brookhaven',
      'center_city': 'Center City',
      'center_city_east': 'Center City East',
      'havertown': 'Havertown',
      'ne_philadelphia': 'NE Philadelphia',
      'pcom': 'PCOM',
      'port_richmond': 'Port Richmond',
      'radnor': 'Radnor',
      'ridley': 'Ridley',
      'roxborough': 'Roxborough',
      'south_philadelphia': 'South Philadelphia',
      'spring_garden': 'Spring Garden',
      'university_city': 'University City',
      'west_philadelphia': 'West Philadelphia',
      'bridgewater': 'Bridgewater',
      'halifax': 'Halifax',
      'north_easton': 'North Easton',
      'stoughton': 'Stoughton',
      'central_point': 'Central Point',
      'eagle_point': 'Eagle Point',
      'north_medford': 'North Medford',
      'grants_pass': 'Grants Pass',
      'ashland': 'Ashland',
      'south_medford': 'South Medford'
    };
    
    return locationDisplayNames[location.toLowerCase()] || location;
  };
  
  // Only show dropdown if user has access to multiple locations
  if (availableLocations.length <= 1) {
    return (
      <div className="flex items-center space-x-2 px-3 py-2 bg-teal-700 text-white rounded-lg">
        <MapPin className="w-4 h-4" />
        <span>{formatLocationName(currentLocation)}</span>
      </div>
    );
  }
  
  return (
    <div className="relative">
      <button
        className="flex items-center space-x-2 px-3 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 transition-colors"
        onClick={() => setDropdownOpen(!dropdownOpen)}
      >
        <MapPin className="w-4 h-4" />
        <span>{formatLocationName(currentLocation)}</span>
        <ChevronDown className="w-4 h-4" />
      </button>
      
      {dropdownOpen && (
        <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg z-20 border border-gray-200">
          {availableLocations.map(location => (
            <button
              key={location}
              className={`block w-full text-left px-4 py-2 ${currentLocation === location ? 'bg-teal-50 text-teal-700' : 'text-gray-700 hover:bg-gray-50'}`}
              onClick={() => {
                onLocationChange(location);
                setDropdownOpen(false);
              }}
            >
              {formatLocationName(location)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const TabButton = ({ label, isActive, count, onClick }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 font-medium rounded-t-lg transition-colors ${
      isActive 
        ? 'bg-white text-teal-600' 
        : 'text-gray-600 hover:text-gray-800'
    }`}
  >
    {label}
    <span className="ml-2 px-2 py-0.5 text-sm rounded-full bg-gray-100">
      {label === 'Completed' && count >= 20 ? '20+' : 
       label === 'Current' && count >= 50 ? '50+' : count}
    </span>
  </button>
);

export const Dashboard = () => {
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('current');
  const [showMobileDetail, setShowMobileDetail] = useState(false);
  const [aiResponse, setAiResponse] = useState('');
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [notificationVisible, setNotificationVisible] = useState(false);
  const [notification, setNotification] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [patientsToRemove, setPatientsToRemove] = useState([]);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedPatients, setSelectedPatients] = useState([]);
  const [isCombining, setIsCombining] = useState(false);
  const [newPatientIds, setNewPatientIds] = useState([]);
  const [animatedPatients, setAnimatedPatients] = useState(new Set());
  const { logout } = useAuth();
  const locationInitialized = useRef(false); 
  const [showUpdateBanner, setShowUpdateBanner] = useState(() => {
    // Check localStorage for banner dismissal status
    return localStorage.getItem('updateBannerDismissed') !== 'true';
  });

  // Handle banner dismissal
  const handleDismissBanner = () => {
    setShowUpdateBanner(false);
    localStorage.setItem('updateBannerDismissed', 'true');
  };

  // Default settings configuration
  // Default settings configuration
  const defaultSettings = {
    suggestPOCTests: false,
    hpiFormat: 'paragraph',
    showROS: true,
    showPhysicalExam: false,
    showAdditionalMDM: false,
    showVisitSummary: true,
    visitSummaryFormat: 'bullet',
    includeVisitSummaryMeds: true,
    showICDCodes: true,
    showCPTCodes: true,
    showEMCodes: false,  // E/M Codes disabled by default
    showSnomedCodes: true,
    numberedAssessmentPlan: false,
    customSignOff: '',
    useBlainesPrompts: false,  // Add Blaine's Prompts toggle for speer location
    maView: false,  // MA View - makes all fields read-only except name
    onlyShowTodaysPatients: false,
    //transcriptionLanguage: 'multi',
  };
  
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Settings query - Fetches from server with local fallback
  const { 
    data: userSettings = defaultSettings, 
    isLoading: isSettingsLoading 
  } = useQuery({
    queryKey: ['userSettings'],
    queryFn: fetchUserSettings,
    onError: (error) => {
      if (error.message === 'unauthorized') {
        navigate('/login');
      } else {
        console.error('Error fetching settings:', error);
        // If server fetch fails, try to use localStorage
        const savedSettings = localStorage.getItem('fluencyUserSettings');
        if (savedSettings) {
          try {
            return JSON.parse(savedSettings);
          } catch (e) {
            console.error('Error parsing saved settings:', e);
            return defaultSettings;
          }
        }
      }
    }
  });
  
  // Settings mutation - Saves to server and localStorage
  const saveSettingsMutation = useMutation({
    mutationFn: saveUserSettings,
    onSuccess: (data) => {
      // Update React Query cache
      queryClient.setQueryData(['userSettings'], data.settings);
      
      // Also save to localStorage as backup
      localStorage.setItem('fluencyUserSettings', JSON.stringify(data.settings));
      
      //setNotification('Settings saved successfully');
      setNotificationVisible(true);
      setTimeout(() => setNotificationVisible(false), 3000);
    },
    onError: (error) => {
      console.error('Error saving settings:', error);
      
      // Still save to localStorage even if server save fails
      localStorage.setItem('fluencyUserSettings', JSON.stringify(userSettings));
      
      setNotification('Settings saved locally only. Server sync failed.');
      setNotificationVisible(true);
    }
  });
  
  // Handle saving settings
  const handleSaveSettings = (newSettings) => {
    saveSettingsMutation.mutate(newSettings);
  };

  const handleMultiPatientsDetected = (detectedPatients) => {
    console.log("Multi-patients detected callback received:", detectedPatients);
    
    queryClient.invalidateQueries(['patients', currentLocation]);
    
    if (detectedPatients && detectedPatients.length > 0) {
      const firstPatient = detectedPatients[0];
      if (firstPatient.id) {
        setTimeout(() => {
          setSelectedPatientId(firstPatient.id);
        }, 500);
      }
    }
    
    setNotification(`${detectedPatients.length} patients automatically detected and created`);
    setNotificationVisible(true);
    setTimeout(() => setNotificationVisible(false), 3000);
  };
  
  const refreshPatientList = () => {
    console.log("Refreshing patient list");
    queryClient.invalidateQueries(['patients', currentLocation], {
      refetchActive: true,
      refetchInactive: true
    });
  };

  // Fetch user account with React Query
  const { data: userAccount, isError } = useQuery({
    queryKey: ['userAccount'],
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const response = await fetchWithNoCache('/api/user/account', {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to fetch user account');
      const data = await response.json();
      console.log('User account data received:', data);
      return data;
    },
    retry: false // Prevent retrying on error
  });

  // Handle user account data and set location (only once on initial load)
  useEffect(() => {
    if (userAccount && userAccount.locations && userAccount.locations.length > 0 && !locationInitialized.current) {
      console.log('🎯 Initializing location from account data');
      console.log('   Last accessed:', userAccount.last_accessed_location);
      console.log('   Default:', userAccount.default_location);
      console.log('   Available:', userAccount.locations);

      setAvailableLocations(userAccount.locations);

      // Set current location with priority: last_accessed_location > default_location > first available
      if (userAccount.last_accessed_location && userAccount.locations.includes(userAccount.last_accessed_location)) {
        console.log('✅ Using LAST ACCESSED location:', userAccount.last_accessed_location);
        setCurrentLocation(userAccount.last_accessed_location);
        locationInitialized.current = true;
      } else if (userAccount.default_location && userAccount.locations.includes(userAccount.default_location)) {
        console.log('⚠️  Using DEFAULT location:', userAccount.default_location);
        setCurrentLocation(userAccount.default_location);
        locationInitialized.current = true;
      } else {
        console.log('⚠️  Using FIRST AVAILABLE location:', userAccount.locations[0]);
        setCurrentLocation(userAccount.locations[0]);
        locationInitialized.current = true;
      }
    }
  }, [userAccount]);

  // Handle authentication errors
  useEffect(() => {
    if (isError) {
      navigate('/login');
    }
  }, [isError, navigate]);

  // Session keep-alive mechanism
  useEffect(() => {
    // Function to ping the server and keep session alive
    const keepAlive = () => {
      fetchWithNoCache('/api/ping', {
        credentials: 'include'
      })
      .then(response => {
        if (!response.ok) {
          console.log('Session may have expired, attempting to refresh');
          // If we get an error, try to refresh data
          queryClient.invalidateQueries(['patients']);
          queryClient.invalidateQueries(['userAccount']);
          queryClient.invalidateQueries(['userSettings']);
        }
      })
      .catch(error => {
        console.error('Error in keep-alive ping:', error);
      });
    };

    // Set up interval for keep-alive (every 5 minutes)
    const keepAliveInterval = setInterval(keepAlive, 5 * 60 * 1000);
    
    // Set up user activity detection
    const resetInactivityTimer = () => {
      // Reset any inactivity timers here if needed
      // This helps prevent logout during active use
      keepAlive();
    };
    
    // Monitor user activity
    window.addEventListener('mousemove', resetInactivityTimer);
    window.addEventListener('keypress', resetInactivityTimer);
    window.addEventListener('click', resetInactivityTimer);
    window.addEventListener('touchstart', resetInactivityTimer);
    
    // Call immediately to refresh session
    keepAlive();
    
    // Clean up on component unmount
    return () => {
      clearInterval(keepAliveInterval);
      window.removeEventListener('mousemove', resetInactivityTimer);
      window.removeEventListener('keypress', resetInactivityTimer);
      window.removeEventListener('click', resetInactivityTimer);
      window.removeEventListener('touchstart', resetInactivityTimer);
    };
  }, [queryClient]);
  
  // Add a reconnection handler for when the app becomes active after inactivity
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('App visible again, refreshing data');
        // Refresh data when the app becomes visible again after being in background
        queryClient.invalidateQueries(['patients', currentLocation]);
        queryClient.invalidateQueries(['userAccount']);
        queryClient.invalidateQueries(['userSettings']);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [queryClient, currentLocation]);

  // Create unique tab ID for handling multiple browser tabs
  useEffect(() => {
    // Create a unique tab ID
    const tabId = Math.random().toString(36).substring(2, 15);
    
    // Function to broadcast tab activity
    const broadcastActivity = () => {
      localStorage.setItem('lastActiveTab', tabId);
      localStorage.setItem('lastActivity', Date.now().toString());
    };
    
    // Broadcast activity periodically
    const interval = setInterval(broadcastActivity, 5000);
    
    // Initial broadcast
    broadcastActivity();
    
    // Clean up
    return () => clearInterval(interval);
  }, []);

  // Handle online/offline status
  useEffect(() => {
    const handleOnlineStatus = () => {
      if (navigator.onLine) {
        console.log('Back online, refreshing data...');
        queryClient.invalidateQueries(['patients', currentLocation]);
        queryClient.invalidateQueries(['userAccount']);
        queryClient.invalidateQueries(['userSettings']);
      }
    };
    
    window.addEventListener('online', handleOnlineStatus);
    return () => window.removeEventListener('online', handleOnlineStatus);
  }, [queryClient, currentLocation]);

  // Check authentication status periodically
  useEffect(() => {
    const checkSessionStatus = async () => {
      try {
        const response = await fetch('/api/check-auth', {
          credentials: 'include'
        });
        console.log('Session status check:', response.ok ? 'authenticated' : 'not authenticated');
      } catch (error) {
        console.error('Session check error:', error);
      }
    };
    
    checkSessionStatus();
    const interval = setInterval(checkSessionStatus, 60000); // Check every minute
    
    return () => clearInterval(interval);
  }, []);

  // Fetch patients with React Query - now depends on location
  const { data: patients = [], isLoading, error } = useQuery({
    queryKey: ['patients', currentLocation],
    queryFn: () => {
      console.log("Fetching patients with location:", currentLocation);
      return currentLocation ? fetchPatients(currentLocation) : [];
    },
    enabled: !!currentLocation,
    //refetchInterval: 3000,
    //refetchOnWindowFocus: true, // Enable refetch on window focus
    //refetchOnReconnect: true,   // Enable refetch on network reconnect
    refetchInterval: 10000,
    refetchOnWindowFocus: true,
    refetchOnMount: false,
    staleTime: 10000,           // Consider data stale after 10 seconds
    retry: 3,                   // Retry failed requests 3 times
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    onError: (error) => {
      if (error.message === 'unauthorized') {
        navigate('/login');
      }
      console.error('Error fetching patients:', error);
    }
  });

  // Add timezone handling functions
const getLocationTimezone = (location) => {
  const loc = (location || 'evergreen').toLowerCase().trim();
  const timezoneMap = {
    'evergreen': 'America/Los_Angeles',
    'siliconbeach': 'America/Los_Angeles',
    'weho': 'America/Los_Angeles',
    'brentwood': 'America/Los_Angeles',
    'skindrmbw': 'America/Los_Angeles',
    'skindrmweho': 'America/Los_Angeles',
    'dtla': 'America/Los_Angeles',
    'oceanpark': 'America/Los_Angeles',
    'confdemo': 'America/New_York',
    'cedarhills': 'America/Los_Angeles',
    'hillsboro': 'America/Los_Angeles',
    'hallandale': 'America/New_York',
    'speer': 'America/Denver',
    'wylie': 'America/Chicago', // Central Time (Dallas)
    'iec': 'America/Chicago',
    'lincoln': 'America/Chicago',
    'wicker': 'America/Chicago',
    'logan': 'America/Chicago',
    '2418': 'America/Chicago',
    'diversey': 'America/Chicago',
    'brockton': 'America/New_York',
    'sonoma':'America/Los_Angeles',
    'napa':'America/Los_Angeles',
    'benicia':'America/Los_Angeles',
    'petaluma':'America/Los_Angeles',
    'miracletouch':'America/Los_Angeles',
    'modernmdfulton': 'America/New_York',
    'modernmdflatbush': 'America/New_York',
    'modernmdfreshpond': 'America/New_York',
    'modernmdgraham': 'America/New_York',
    'modernmdjh': 'America/New_York',
    'modernmdjamaica': 'America/New_York',
    'modernmdknickerbrocker': 'America/New_York',
    'modernmdlefferts': 'America/New_York',
    'modernmdlibertyave': 'America/New_York',
    'modernmdnorthutica': 'America/New_York',
    'modernmdsunsetpark': 'America/New_York',
    'modernmdutica': 'America/New_York',
    'pulsemd': 'America/New_York',
    'wappingers': 'America/New_York',
    'mahopac': 'America/New_York',
    'mohegan_lake': 'America/New_York',
    'poughkeepsie': 'America/New_York',
    'hudson_heritage': 'America/New_York',
    'briarcliff': 'America/New_York',
    'bensalem': 'America/New_York',
    'blue_bell': 'America/New_York',
    'brookhaven': 'America/New_York',
    'center_city': 'America/New_York',
    'center_city_east': 'America/New_York',
    'havertown': 'America/New_York',
    'ne_philadelphia': 'America/New_York',
    'pcom': 'America/New_York',
    'port_richmond': 'America/New_York',
    'radnor': 'America/New_York',
    'ridley': 'America/New_York',
    'roxborough': 'America/New_York',
    'south_philadelphia': 'America/New_York',
    'spring_garden': 'America/New_York',
    'university_city': 'America/New_York',
    'west_philadelphia': 'America/New_York',
    'bridgewater': 'America/New_York',
    'halifax': 'America/New_York',
    'north_easton': 'America/New_York',
    'stoughton': 'America/New_York',
    'central_point': 'America/Los_Angeles',
    'eagle_point': 'America/Los_Angeles',
    'north_medford': 'America/Los_Angeles',
    'grants_pass': 'America/Los_Angeles',
    'ashland': 'America/Los_Angeles',
    'south_medford': 'America/Los_Angeles'
  };
  return timezoneMap[loc] || 'America/Los_Angeles';
};

// Format date/time in location's timezone
const formatDateTimeForLocation = (date, location) => {
  const timezone = getLocationTimezone(location);
  
  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    month: '2-digit',
    day: '2-digit',
    year: '2-digit'
  });
  const dateStr = dateFormatter.format(date);
  
  const timeFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
  const timeStr = timeFormatter.format(date);
  
  return { dateStr, timeStr };
};

  // Create patient mutation (now includes location)
  // In your Dashboard.jsx file

  const createPatientMutation = useMutation({
    mutationFn: () => createPatient(currentLocation),
    
    onMutate: async () => {
      const stableId = `stable-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      await queryClient.cancelQueries(['patients', currentLocation]);
      
      const previousPatients = queryClient.getQueryData(['patients', currentLocation]) || [];
      const now = new Date();
      
      // Use timezone-aware formatting
      const { dateStr, timeStr } = formatDateTimeForLocation(now, currentLocation);
      
      const optimisticPatient = {
        id: `temp-${stableId}`,
        stableId: stableId,
        patient_id: `temp-${stableId}`,
        name: 'First Last',
        first_name: 'First',
        last_name: 'Last',
        dob: '01/01/2025',
        date: dateStr,  // Use location-specific date
        time: timeStr,  // Use location-specific time
        has_soap_note: false,
        sentToEHR: false,
        isScheduled: false,
        location: currentLocation,
        created_at: now.toISOString(),
        displayTime: now.toISOString(),
        _isNew: true
      };
      
      queryClient.setQueryData(['patients', currentLocation], 
        [optimisticPatient, ...previousPatients]
      );
      
      setSelectedPatientId(optimisticPatient.id);
      setShowMobileDetail(true);
      
      return { previousPatients, stableId };
    },
    
    // Keep your existing onSuccess and onError handlers but update the patient object creation
    onSuccess: async (createdPatient, _, context) => {
      try {
        const soapResult = await createSoapNote(createdPatient.id);
        
        const updatedPatient = {
          ...soapResult,
          stableId: context.stableId
        };
        
        queryClient.setQueryData(['patients', currentLocation], oldData => {
          if (!oldData) return [updatedPatient];
          
          return oldData.map(patient => 
            patient.stableId === context.stableId ? updatedPatient : patient
          );
        });
        
        setSelectedPatientId(createdPatient.id);
        
      } catch (error) {
        console.error('Error creating SOAP note:', error);
        
        const updatedPatient = {
          ...createdPatient,
          stableId: context.stableId
        };
        
        queryClient.setQueryData(['patients', currentLocation], oldData => {
          if (!oldData) return [updatedPatient];
          return oldData.map(patient => 
            patient.stableId === context.stableId ? updatedPatient : patient
          );
        });
        
        setSelectedPatientId(createdPatient.id);
      }
    },
    
    onError: (err, _, context) => {
      console.error('Error creating patient:', err);
      queryClient.setQueryData(['patients', currentLocation], context.previousPatients);
      setNotification("Failed to create patient");
      setNotificationVisible(true);
    }
  });

  // Handle location change
  const handleLocationChange = async (newLocation) => {
    console.log('📍 User switching location to:', newLocation);
    setCurrentLocation(newLocation);
    setSelectedPatientId(null); // Clear selected patient when changing location
    setShowMobileDetail(false); // Go back to list view on mobile

    // Update the last accessed location on the server
    try {
      const response = await fetch('/api/user/update-location', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ location: newLocation })
      });
      const result = await response.json();
      console.log('💾 Location saved to backend:', result.last_accessed_location);
    } catch (error) {
      console.error('❌ Failed to update last accessed location:', error);
      // Don't show error to user as this is a background operation
    }
  };

  // Helper function to check if a date is today
  const isToday = (dateString) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const today = new Date();
    return date.getFullYear() === today.getFullYear() &&
           date.getMonth() === today.getMonth() &&
           date.getDate() === today.getDate();
  };

  // Replace the existing displayedPatients function with this debug version
// This version adds console logs to track what's happening

const displayedPatients = React.useMemo(() => {
  const shouldFilterByDate = userSettings?.onlyShowTodaysPatients && activeTab === 'current';

  return patients.filter(patient => {
    const matchesSearch = patient.name?.toLowerCase().includes(searchQuery.toLowerCase());
    // For completed tab, a patient is considered complete if sentToEHR is true or if (isScheduled and questionnaire_completed_at exists)
    const matchesTab = activeTab === 'current'
      ? (!patient.sentToEHR && (!patient.isScheduled || !patient.questionnaire_completed_at))
      : (patient.sentToEHR || (patient.isScheduled && !!patient.questionnaire_completed_at));

    // When enabled on the current tab, only show patients created today
    const patientDate = patient.displayTime || patient.created_at || patient.date;
    const matchesDate = shouldFilterByDate ? isToday(patientDate) : true;

    return matchesSearch && matchesTab && matchesDate;
  });
}, [patients, searchQuery, activeTab, userSettings]);

  // Get counts for the tabs
  const currentCount = patients.filter(p => !p.sentToEHR).length;
  const completedCount = patients.filter(p => p.sentToEHR).length;

  // Get the selected patient
  const selectedPatient = patients.find(p => p.id === selectedPatientId);

  // Clear AI response and chat history when patient changes
  useEffect(() => {
    setAiResponse('');

    // Clear the backend chat history when switching patients
    const clearChatHistory = async () => {
      try {
        await fetch('/api/ai/clear-history', {
          method: 'POST',
          credentials: 'include'
        });
      } catch (error) {
        console.error('Error clearing chat history:', error);
      }
    };

    if (selectedPatientId) {
      clearChatHistory();
    }
  }, [selectedPatientId]);

  // Handle tab change
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    const patientsInNewTab = patients.filter(p => 
      newTab === 'current' ? !p.sentToEHR : p.sentToEHR
    );
    if (patientsInNewTab.length > 0) {
      setSelectedPatientId(patientsInNewTab[0].id);
    } else {
      setSelectedPatientId(null);
    }
  };

  // Handle creating a new patient
  const handleCreateNewPatient = () => {
    if (currentLocation) {
      // Pause automatic refetching
      queryClient.cancelQueries(['patients', currentLocation]);
      
      createPatientMutation.mutate();
      
      // Prevent refetching for 30 seconds after creating a patient
      const previousOptions = queryClient.getDefaultOptions();
      queryClient.setDefaultOptions({
        queries: {
          ...previousOptions.queries,
          refetchInterval: false,
        },
      });
      
      // Restore original refetch settings after 30 seconds
      setTimeout(() => {
        queryClient.setDefaultOptions(previousOptions);
        // Force a manual refresh
        queryClient.invalidateQueries(['patients', currentLocation]);
      }, 30000);
    } else {
      setNotification("Please select a location before creating a patient");
      setNotificationVisible(true);
    }
  };

  // Handle patient updates
  const handlePatientUpdate = (updatedPatient) => {
    console.log("Patient update received:", updatedPatient);
    
    if (updatedPatient && updatedPatient.is_combined) {
      setSelectedPatientId(updatedPatient.id);
      setShowMobileDetail(true);
      
      // Invalidate the cache first
      queryClient.invalidateQueries(['patients', currentLocation]);
      
      // Then update the cache with new data
      queryClient.setQueryData(
        ['patients', currentLocation],
        (oldData = []) => {
          const existingPatient = oldData.find(p => p.id === updatedPatient.id);
          const deduped = oldData.filter(p => p.id !== updatedPatient.id);
          
          // Preserve CPT codes from existing patient if they're missing in the update
          const patientWithPreservedData = {
            ...updatedPatient,
            cpt_codes: updatedPatient.cpt_codes || (existingPatient && existingPatient.cpt_codes)
          };
          
          const combined = [patientWithPreservedData, ...deduped];
          
          return combined.sort((a, b) => {
            const dateA = new Date(a.created_at || a.date);
            const dateB = new Date(b.created_at || b.date);
            return dateB - dateA;
          });
        }
      );
    } else {
      // Invalidate the cache first
      queryClient.invalidateQueries(['patients', currentLocation]);
      
      // Then update the cache with new data
      queryClient.setQueryData(['patients', currentLocation], (oldData = []) => {
        const updated = oldData.map(p => 
          p.id === updatedPatient.id ? { 
            ...p, 
            ...updatedPatient,
            // Preserve CPT codes if they exist in the old data but are missing in the update
            cpt_codes: updatedPatient.cpt_codes || p.cpt_codes,
            stableId: p.stableId 
          } : p
        );
        
        return updated.sort((a, b) => {
          const dateA = new Date(a.created_at || a.date);
          const dateB = new Date(b.created_at || b.date);
          return dateB - dateA;
        });
      });
      
      // Force a refetch to ensure we have the latest data
      // TEMPORARY FIX: Comment out to prevent overwriting CPT codes that may not be returned by backend
      // queryClient.refetchQueries(['patients', currentLocation]);
    }
  };
  // Handle patient deletion
  const handleDeletePatient = async (deletedPatientId) => {
    try {
      // Optimistically remove the patient from the UI first
      const previousPatients = queryClient.getQueryData(['patients', currentLocation]) || [];
      
      // Update the data and let Framer Motion handle the exit animation
      queryClient.setQueryData(['patients', currentLocation], 
        previousPatients.filter(p => p.id !== deletedPatientId)
      );
      
      // Handle selection state changes
      if (selectedPatientId === deletedPatientId) {
        const remainingPatients = previousPatients.filter(p => p.id !== deletedPatientId);
        if (remainingPatients.length > 0) {
          setSelectedPatientId(remainingPatients[0].id);
        } else {
          setSelectedPatientId(null);
          setShowMobileDetail(false);
        }
      }
      
      // Actually perform the delete (after animation started)
      await fetch(`/api/patients/${deletedPatientId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
    } catch (error) {
      console.error('Error deleting patient:', error);
      
      // Refetch to restore correct state if delete failed
      queryClient.invalidateQueries(['patients', currentLocation]);
      
      setNotification('Failed to delete patient');
      setNotificationVisible(true);
    }
  };

  // Handle EHR status change
  const handleEHRStatusChange = (patientId, isSentToEHR) => {
    queryClient.setQueryData(['patients', currentLocation], (oldData = []) => {
      return oldData.map(p => 
        p.id === patientId ? { ...p, sentToEHR: isSentToEHR } : p
      );
    });

    // If the patient is now completed and we're in the current tab,
    // select another current patient if available
    if (isSentToEHR && activeTab === 'current') {
      const remainingCurrentPatients = patients.filter(p => 
        !p.sentToEHR && p.id !== patientId
      );

      if (remainingCurrentPatients.length > 0) {
        setSelectedPatientId(remainingCurrentPatients[0].id);
      } else {
        setSelectedPatientId(null);
      }
    }
  };

  // Handle patient selection
  // Enhanced patient selection handler
const handlePatientSelection = (patient) => {
  if (isSelectMode) {
    if (patient.is_combined) {
      setNotification("Can't combine patients that are already combined");
      setNotificationVisible(true);
      setTimeout(() => setNotificationVisible(false), 3000);
      return;
    }
    
    if (selectedPatients.some(p => p.is_combined)) {
      setNotification("Can't combine with patients that are already combined");
      setNotificationVisible(true);
      setTimeout(() => setNotificationVisible(false), 3000);
      return;
    }
    
    if (selectedPatients.some(p => p.id === patient.id)) {
      setSelectedPatients(prev => prev.filter(p => p.id !== patient.id));
    } else {
      setSelectedPatients(prev => [...prev, patient]);
    }
  } else {
    setSelectedPatientId(patient.id);
    setShowMobileDetail(true);
  }
};

// Combine patients function
const handleCombinePatients = async () => {
  if (selectedPatients.length < 2) {
    setNotification("Select at least two patients to combine");
    setNotificationVisible(true);
    return;
  }
  
  setIsCombining(true);
  
  try {
    const response = await fetch('/api/patients/combine', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        patient_ids: selectedPatients.map(p => p.id),
        location: currentLocation
      })
    });
    
    if (!response.ok) {
      throw new Error('Failed to combine patients');
    }
    
    const combinedPatient = await response.json();
    
    queryClient.setQueryData(['patients', currentLocation], (oldData = []) => {
      return [combinedPatient, ...oldData];
    });
    
    setIsSelectMode(false);
    setSelectedPatients([]);
    setSelectedPatientId(combinedPatient.id);
    setShowMobileDetail(true);
    
    setNotification("Patients combined successfully");
    setNotificationVisible(true);
    setTimeout(() => setNotificationVisible(false), 3000);
  } catch (error) {
    console.error('Error combining patients:', error);
    setNotification("Failed to combine patients");
    setNotificationVisible(true);
  } finally {
    setIsCombining(false);
  }
};
// Add function to separate combined patients
const handleSeparatePatients = async (combinedId) => {
  try {
    const response = await fetch(`/api/patients/separate/${combinedId}`, {
      method: 'POST',
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error('Failed to separate patients');
    }
    
    // Remove the combined patient from cache
    queryClient.setQueryData(['patients', currentLocation], (oldData = []) => {
      return oldData.filter(p => p.id !== combinedId);
    });
    
    setSelectedPatientId(null);
    setShowMobileDetail(false);
    
    setNotification("Patients separated successfully");
    setNotificationVisible(true);
  } catch (error) {
    console.error('Error separating patients:', error);
    setNotification("Failed to separate patients");
    setNotificationVisible(true);
  }
};

  // Handle back to list
  const handleBackToList = () => {
    setShowMobileDetail(false);
  };

  // Handle asking AI
  const handleAskAI = async (query) => {
    try {
      setIsAiLoading(true);

      // Get the CURRENT patient ID at the time of the query
      const currentPatientId = selectedPatientId;

      // Validate that we have a patient ID before proceeding
      if (!currentPatientId) {
        throw new Error('No patient selected');
      }

      // Find the patient from the current patients array using the ID
      const currentPatient = patients.find(p => p.id === currentPatientId);

      // Validate that we found the patient
      if (!currentPatient) {
        throw new Error('Patient not found in current list');
      }

      console.log('AI Query - Patient ID:', currentPatientId);
      console.log('AI Query - Patient Name:', currentPatient.name);

      // Construct patient data BEFORE clearing history to avoid timing issues
      const patientData = {
        patientInfo: {
          name: currentPatient.name,
          dob: currentPatient.dob,
          sex: currentPatient.sex
        },
        visitInfo: {
          hpi: currentPatient.hpi || '',
          assessment_plan: currentPatient.assessment_plan || '',
          ros: currentPatient.ros || '',
          physical_exam: currentPatient.physical_exam || '',
          suggested_tests: currentPatient.suggested_tests || '',
          after_visit_summary: currentPatient.visit_summary || '',
          icd_codes: currentPatient.icd_codes || ''
        }
      };

      console.log('Sending data to API:', {
        prompt: query,
        patientData: patientData
      });

      // Note: We don't clear history here anymore - the backend automatically
      // detects patient changes and clears history when needed. This allows
      // for follow-up questions to maintain conversation context.

      // Make API call to your Flask backend
      const response = await fetch('/api/ai/query', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          prompt: query,
          patientData: patientData
        })
      });

      // Log the raw response
      const responseText = await response.text();
      console.log('Raw API Response:', responseText);

      // Parse the response only if it's valid JSON
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        throw new Error(`Invalid JSON response: ${responseText}`);
      }

      if (!response.ok) {
        throw new Error(data.error || 'AI request failed');
      }

      // IMPORTANT: Only update the response if we're still on the same patient
      // This prevents race conditions where a slow response from patient A
      // overwrites the display when we're now viewing patient B
      if (selectedPatientId === currentPatientId) {
        setAiResponse(data.response);
      } else {
        console.log('Discarding stale AI response for patient:', currentPatientId);
      }
    } catch (error) {
      console.error('Error querying AI:', error);
      setAiResponse('Sorry, I encountered an error processing your request: ' + error.message);
    } finally {
      setIsAiLoading(false);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      // Use the auth context for logout
      const result = await logout();
      
      if (result.success) {
        // Clear any local state
        queryClient.clear();
        localStorage.removeItem('fluencyUserSettings');
        
        // Force a complete page reload or navigate to login
        window.location.href = '/login';
      } else {
        console.error('Logout failed:', result.error);
        setNotification('Logout failed. Please try again.');
        setNotificationVisible(true);
      }
    } catch (error) {
      console.error('Error logging out:', error);
      setNotification('Logout error. Please try again.');
      setNotificationVisible(true);
    }
  };

  // Handle notification close
  const handleCloseNotification = () => {
    setNotificationVisible(false);
  };

  // Check if we're in Evergreen-only mode - always false since we only have Evergreen
  const isEvergreenOnly = false;

  return (
      <div className="min-h-screen bg-gray-50">
        {/* Update Banner */}
        {/*showUpdateBanner && currentLocation !== 'siliconbeach' && (
          <div className="bg-teal-600 text-white py-2 px-4 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="font-medium">New updates!</span>
              <span className="text-sm">Check out the latest features and improvements. Paste your favorite notes and press 'Learn Format'. <a href="https://forms.gle/cGCAja3mqDKioixX8" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-200">Let us know what you think.</a></span>
            </div>
            <button
              onClick={handleDismissBanner}
              className="text-white hover:text-gray-200 focus:outline-none"
              aria-label="Dismiss update banner"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        )*/}
      <header style={{ backgroundColor: '#2d6b78' }} className="text-white p-2">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setIsSidebarVisible(!isSidebarVisible)}
              className="p-2 text-white rounded-lg hover:bg-teal-700 transition-colors hidden min-[1050px]:block"
            >
              <Menu className="w-6 h-6" />
            </button>
            <img src={fluencyLogo} alt="Preface" className="h-10" />
          </div>
          
          <div className="flex items-center space-x-4">
            <LocationSelector
              currentLocation={currentLocation}
              availableLocations={availableLocations}
              onLocationChange={handleLocationChange}
            />

            <SupportButton
              currentPatient={selectedPatient}
              currentLocation={currentLocation}
            />

            <button
              onClick={() => setIsSettingsOpen(true)}
              className="px-3 py-2 text-white rounded-lg hover:bg-teal-700 transition-colors"
              aria-label="Settings"
            >
              <Settings className="w-5 h-5" />
            </button>

            <button
              onClick={handleLogout}
              className="px-4 py-2 text-white rounded hover:underline transition-colors"
            >
              Log Out
            </button>
          </div>
        </div>
      </header>
      
      {location !== 'siliconbeach' && (
        <SettingsModal
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          settings={userSettings}
          onSettingsChange={handleSaveSettings}
          currentLocation={currentLocation}
        />
      )}

      {notificationVisible && notification && (
        <div className="container mx-auto px-4 mt-2">
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 flex justify-between items-center">
            <p className="text-blue-700">{notification}</p>
            <button onClick={handleCloseNotification}>
              <X className="w-5 h-5 text-blue-500" />
            </button>
          </div>
        </div>
      )}

      <main className="container mx-auto py-6 px-4">
        {currentLocation ? (
          <div className="grid grid-cols-1 min-[1050px]:grid-cols-10 gap-4">
            {/* Patient List Section - Hidden on mobile when detail is shown */}
            <div 
              className={`
                min-[1050px]:col-span-3 
                ${showMobileDetail ? 'hidden min-[1050px]:block' : 'block'}
                ${!isSidebarVisible ? 'hidden min-[1050px]:hidden' : ''}
              `}
            >
              <div className="relative mb-4 flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Find patient"
                    className="w-full pl-10 pr-4 py-2 border rounded-lg"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <button
                  onClick={handleCreateNewPatient}
                  className="px-2 py-2 rounded-lg flex items-center justify-center bg-stone-50 hover:bg-gray-100"
                >
                  <Plus className="w-6 h-6" style={{ color: "#68B9B0" }} />
                </button>
              </div>
              
              {/* Tabs */}
              <div className="mb-4">
                <div className="flex space-x-4">
                  <TabButton
                    label="Current"
                    isActive={activeTab === 'current'}
                    count={currentCount}
                    onClick={() => handleTabChange('current')}
                  />
                  <TabButton
                    label="Completed"
                    isActive={activeTab === 'completed'}
                    count={completedCount}
                    onClick={() => handleTabChange('completed')}
                  />
                </div>
              </div>

              {/* Selection Controls */}
              {isLoading ? null : (
                <div className="flex justify-between items-center mb-4">
                  <button
                    onClick={() => {
                      setIsSelectMode(!isSelectMode);
                      if (isSelectMode) setSelectedPatients([]);
                    }}
                    className={`px-3 py-1 rounded-md ${
                      isSelectMode ? 'bg-gray-200 text-gray-800' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {isSelectMode ? 'Cancel Selection' : 'Combine Visits'}
                  </button>
                  
                  {isSelectMode && (
                    <button
                      onClick={handleCombinePatients}
                      disabled={selectedPatients.length < 2 || isCombining}
                      className={`px-3 py-1 rounded-md ${
                        selectedPatients.length < 2 || isCombining
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-teal-500 text-white hover:bg-teal-600'
                      }`}
                    >
                      {isCombining ? (
                        <span className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Combining...
                        </span>
                      ) : (
                        `Combine (${selectedPatients.length})`
                      )}
                    </button>
                  )}
                </div>
              )}

              <div className="relative flex flex-col h-[calc(100vh-300px)]">
                <div className="flex-1 overflow-y-auto space-y-2 pb-4 pr-4">
                  {isLoading ? (
                    <div className="flex justify-center items-center py-4">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
                    </div>
                  ) : displayedPatients.length === 0 ? (
                    <div className="text-center py-4 text-gray-500">
                      {searchQuery ? 'No patients found matching your search' : 'No patients available'}
                    </div>
                  ) : (
                  <div className="space-y-2">
                    <AnimatePresence initial={false} mode="sync">
                    {displayedPatients.map(patient => (
                      <motion.div
                        key={patient.stableId || patient.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ 
                          duration: 0,
                          ease: "linear"
                        }}
                      >
                        <div className="relative">
                          {isSelectMode && (
                            <div className="absolute left-2 top-1/2 transform -translate-y-1/2 z-10">
                              <input
                                type="checkbox"
                                checked={selectedPatients.some(p => p.id === patient.id)}
                                onChange={() => handlePatientSelection(patient)}
                                className={`w-5 h-5 text-teal-500 border-gray-300 rounded focus:ring-teal-500 
                                  ${patient.is_combined ? 'opacity-50 cursor-not-allowed' : ''}`}
                                disabled={patient.is_combined || selectedPatients.some(p => p.is_combined)}
                              />
                            </div>
                          )}
                          <div className={`${isSelectMode ? 'pl-8' : ''} 
                            ${isSelectMode && (patient.is_combined || selectedPatients.some(p => p.is_combined)) ? 'opacity-70' : ''}`}>
                            <div className="relative">
                              {/* Wylie-only: Do not text toggle */}
                              {currentLocation === 'wylie' && (
                                <div className="absolute right-2 top-2 flex items-center gap-2 z-10">
                                  <input
                                    type="checkbox"
                                    checked={!!patient.do_not_text}
                                    onChange={async (e) => {
                                      const newValue = e.target.checked;
                                      // Optimistic update
                                      queryClient.setQueryData(['patients', currentLocation], (oldData = []) => {
                                        return oldData.map(p => p.id === patient.id ? { ...p, do_not_text: newValue } : p);
                                      });
                                      try {
                                        await fetch(`/api/patients/${patient.id}`, {
                                          method: 'PATCH',
                                          headers: { 'Content-Type': 'application/json' },
                                          credentials: 'include',
                                          body: JSON.stringify({ do_not_text: newValue })
                                        });
                                      } catch (err) {
                                        console.error('Failed updating do_not_text', err);
                                        // Revert on error
                                        queryClient.invalidateQueries(['patients', currentLocation]);
                                      }
                                    }}
                                    className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                                    title="Do not text this patient"
                                    onClick={(ev) => ev.stopPropagation()}
                                  />
                                  <span className="text-xs text-gray-600 select-none" onClick={(ev) => ev.stopPropagation()}>
                                    Do not text
                                  </span>
                                </div>
                              )}
                              <PatientCard
                                patient={patient}
                                selected={selectedPatientId === patient.id}
                                onClick={() => handlePatientSelection(patient)}
                              />
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                    </AnimatePresence>
                  </div>
                  )}
                </div>
                
                <div className="mt-4">
                  <FluencyAIBar 
                    onAskAI={handleAskAI}
                    aiResponse={aiResponse}
                    isLoading={isAiLoading}
                  />
                </div>
              </div>
            </div>

            {/* Patient Detail Section - Full width on mobile when shown */}
            <div className={`
                ${showMobileDetail ? 'block' : 'hidden min-[1050px]:block'}
                ${isSidebarVisible ? 'min-[1050px]:col-span-7' : 'min-[1050px]:col-span-10'}
              `}>
              {/* Mobile Back Button */}
              <div className="min-[1050px]:hidden mb-4">
                <button
                  onClick={handleBackToList}
                  className="flex items-center text-gray-600 hover:text-gray-800"
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  Back to Patients
                </button>
              </div>

              {selectedPatient ? (
                <PatientDetail 
                patient={selectedPatient}
                onPatientUpdate={handlePatientUpdate}
                onEditingStateChange={setIsEditing}
                onDeletePatient={handleDeletePatient}
                onEHRStatusChange={handleEHRStatusChange}
                userSettings={userSettings}
                onMultiPatientsDetected={handleMultiPatientsDetected}  // Add this
                onPatientListUpdated={refreshPatientList}              // Add this
                currentLocation={currentLocation}                      // Add this
                saveSettingsMutation={saveSettingsMutation}            // Add this
              />
              ) : (
                <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
                  <p>Select a patient to view details</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mb-4 mx-auto"></div>
              <p className="text-gray-600">Loading your dashboard...</p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
